import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  FloppyDisk, Plus, Trash, Eye, EyeSlash, TestTube,
  Bell, EnvelopeSimple, TelegramLogo, WhatsappLogo, PushPin, Key,
  HouseLine, ShieldWarning, Microphone, Copy, Newspaper, HandWaving,
  MapPin, Car, Phone, Image, VideoCamera, Fingerprint
} from '@phosphor-icons/react';
import PasskeysSection from '@/components/PasskeysSection';
import BackupRestoreSection from '@/components/BackupRestoreSection';
import { UsersTab } from '@/components/settings/UsersTab';

export default function SettingsPage() {
  const { api, user, token } = useAuth();
  const { t, lang, changeLang } = useLanguage();
  const push = usePushNotifications(token);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [tab, setTab] = useState('general');

  const isAdmin = user?.role === 'admin';

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSettings(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchSettings(); }, [api]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      setTestResult({ type: 'success', msg: 'Settings saved!' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (err) {
      setTestResult({ type: 'error', msg: err.response?.data?.detail || 'Failed to save' });
    }
    setSaving(false);
  };

  const testChannel = async (channel) => {
    setTestResult({ type: 'info', msg: `Testing ${channel}...` });
    try {
      const res = await api.post(`/notifications/test/${channel}`);
      const result = res.data.results?.[channel];
      setTestResult({ type: result === 'sent' ? 'success' : 'error', msg: `${channel}: ${result}` });
    } catch (err) {
      setTestResult({ type: 'error', msg: `Test failed: ${err.response?.data?.detail || 'Error'}` });
    }
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const updateChannel = (channel, enabled) => {
    setSettings(prev => ({
      ...prev,
      notification_channels: { ...prev.notification_channels, [channel]: enabled }
    }));
  };

  const addCategory = () => {
    const name = prompt('Category name:');
    if (!name) return;
    setSettings(prev => ({
      ...prev,
      expense_categories: [...(prev.expense_categories || []), { id: Date.now().toString(36), name, icon: 'Tag', color: '#4A5D4E' }]
    }));
  };

  const removeCategory = (id) => {
    setSettings(prev => ({
      ...prev,
      expense_categories: prev.expense_categories.filter(c => c.id !== id)
    }));
  };

  const addRole = () => {
    const role = prompt('Staff role name:');
    if (!role) return;
    setSettings(prev => ({
      ...prev,
      staff_roles: [...(prev.staff_roles || []), role]
    }));
  };

  const removeRole = (idx) => {
    setSettings(prev => ({
      ...prev,
      staff_roles: prev.staff_roles.filter((_, i) => i !== idx)
    }));
  };

  if (loading || !settings) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!isAdmin) return <div className="text-center py-20 text-muted-foreground">Only admins can access settings</div>;

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-4xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Admin Panel</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure all parameters from here</p>
        </div>
        <button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn"
          className="px-6 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2 self-start hover:opacity-90 disabled:opacity-50">
          <FloppyDisk size={18} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {testResult && (
        <div className={`rounded-xl px-4 py-3 text-sm font-medium
          ${testResult.type === 'success' ? 'bg-green-500/10 text-green-600 border border-green-500/20' :
            testResult.type === 'error' ? 'bg-red-500/10 text-red-500 border border-red-500/20' :
            'bg-blue-500/10 text-blue-500 border border-blue-500/20'}`} data-testid="settings-feedback">
          {testResult.msg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
        {['general', 'users', 'api-keys', 'alerts', 'messaging', 'bots', 'devices', 'display', 'data', 'backup'].map(tabName => (
          <button key={tabName} onClick={() => setTab(tabName)} data-testid={`settings-tab-${tabName}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${tab === tabName ? 'bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            {tabName === 'api-keys' ? 'API Keys' : tabName === 'alerts' ? 'Alerts' : tabName === 'bots' ? 'Bots' : tabName === 'devices' ? 'Devices' : tabName === 'display' ? 'Display' : tabName === 'data' ? 'Data' : tabName === 'backup' ? 'Backup' : tabName === 'users' ? 'Users' : tabName}
          </button>
        ))}
      </div>

      {/* General */}
      {tab === 'general' && (
        <div className="space-y-6">
          <Section title="App Branding">
            <SettingField label="App Name" value={settings.app_name || 'HomeCommand'} onChange={v => updateSetting('app_name', v)} testId="setting-app-name" hint="Displayed in sidebar, title bar, and TV display" />
            <div>
              <label className="overline block mb-2">App Logo (URL or base64)</label>
              <div className="flex gap-3 items-start">
                {settings.app_logo && (
                  <div className="w-14 h-14 rounded-xl border border-white/10 overflow-hidden bg-white/5 flex items-center justify-center flex-shrink-0">
                    <img src={settings.app_logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <input type="text" value={settings.app_logo || ''} onChange={e => updateSetting('app_logo', e.target.value)}
                    data-testid="setting-app-logo" placeholder="https://... or paste base64 data URL"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" />
                  <div className="flex gap-2 mt-2">
                    <label className="px-3 py-1.5 rounded-lg bg-white/5 text-xs text-neutral-400 cursor-pointer hover:bg-white/10" data-testid="upload-logo-btn">
                      Upload File
                      <input type="file" accept="image/*" className="hidden" onChange={e => {
                        const file = e.target.files?.[0]; if (!file) return;
                        const reader = new FileReader(); reader.onload = () => updateSetting('app_logo', reader.result); reader.readAsDataURL(file);
                      }} />
                    </label>
                    {settings.app_logo && <button onClick={() => updateSetting('app_logo', '')} className="px-3 py-1.5 rounded-lg bg-red-500/10 text-xs text-red-400 hover:bg-red-500/20">Remove</button>}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Favicon (URL or base64)</label>
              <div className="flex gap-3 items-start">
                {settings.app_favicon && (
                  <div className="w-10 h-10 rounded-lg border border-white/10 overflow-hidden bg-white/5 flex items-center justify-center flex-shrink-0">
                    <img src={settings.app_favicon} alt="Favicon" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <input type="text" value={settings.app_favicon || ''} onChange={e => updateSetting('app_favicon', e.target.value)}
                    data-testid="setting-app-favicon" placeholder="https://... or paste base64 data URL"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" />
                  <label className="inline-block mt-2 px-3 py-1.5 rounded-lg bg-white/5 text-xs text-neutral-400 cursor-pointer hover:bg-white/10" data-testid="upload-favicon-btn">
                    Upload File
                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const reader = new FileReader(); reader.onload = () => updateSetting('app_favicon', reader.result); reader.readAsDataURL(file);
                    }} />
                  </label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Recommended: 32x32 or 64x64 PNG</p>
            </div>
            <SettingField label="App Tagline" value={settings.app_tagline || 'House Manager'} onChange={v => updateSetting('app_tagline', v)} testId="setting-app-tagline" hint="Shown under app name in sidebar" />
          </Section>
          <Section title="General Settings">
            <SettingField label="Family Name" value={settings.family_name || ''} onChange={v => updateSetting('family_name', v)} testId="setting-family-name" />
            <SettingField label="Currency" value={settings.currency || 'INR'} onChange={v => updateSetting('currency', v)} testId="setting-currency" />
            <SettingField label="Currency Symbol" value={settings.currency_symbol || 'Rs.'} onChange={v => updateSetting('currency_symbol', v)} testId="setting-currency-symbol" />
            <SettingField label="Timezone" value={settings.timezone || 'Asia/Kolkata'} onChange={v => updateSetting('timezone', v)} testId="setting-timezone" />
            <SettingField label="Morning Briefing Time" value={settings.morning_briefing_time || '07:00'} type="time" onChange={v => updateSetting('morning_briefing_time', v)} testId="setting-briefing-time" />
            <SettingField label="Dietary Preference" value={settings.dietary_preference || 'Jain/Indian Vegetarian'} onChange={v => updateSetting('dietary_preference', v)} testId="setting-dietary-pref" hint="Used for AI meal planner suggestions" />
            <div>
              <label className="overline block mb-2">Language</label>
              <select value={settings.language || lang} onChange={e => { updateSetting('language', e.target.value); changeLang(e.target.value); }}
                data-testid="setting-language"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30">
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="gu">Gujarati</option>
                <option value="mr">Marathi</option>
                <option value="ta">Tamil</option>
                <option value="te">Telugu</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Changes UI labels, notifications, and AI response language</p>
            </div>
          </Section>
          <Section title="Geofencing & Location">
            <p className="text-sm text-muted-foreground mb-4">Configure your home location for staff geofence auto check-in. Use Google Maps to find your coordinates.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SettingField label="Home Latitude" type="number" value={settings.home_latitude ?? 0} onChange={v => updateSetting('home_latitude', parseFloat(v) || 0)} testId="setting-home-lat" hint="e.g., 19.0760 (Mumbai)" />
              <SettingField label="Home Longitude" type="number" value={settings.home_longitude ?? 0} onChange={v => updateSetting('home_longitude', parseFloat(v) || 0)} testId="setting-home-lng" hint="e.g., 72.8777 (Mumbai)" />
            </div>
            <SettingField label="Geofence Radius (meters)" type="number" value={settings.geofence_radius_meters ?? 50} onChange={v => updateSetting('geofence_radius_meters', parseInt(v) || 50)} testId="setting-geofence-radius" hint="Staff within this radius of home are auto checked-in" />
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium">Face Recognition</p>
                <p className="text-xs text-muted-foreground">Enable face-based attendance in Staff Portal</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={settings.face_recognition_enabled ?? true} onChange={e => updateSetting('face_recognition_enabled', e.target.checked)} data-testid="setting-face-toggle" className="sr-only peer" />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>
          </Section>
          <Section title="Two-Factor Authentication (2FA)">
            <TwoFASetup api={api} />
          </Section>
          <Section title="Change Password">
            <ChangePasswordForm api={api} />
          </Section>
          <Section title={<span className="flex items-center gap-2"><Fingerprint size={18} weight="duotone" className="text-[#00E5FF]" />Passkeys & Recovery</span>}>
            <PasskeysSection />
          </Section>
        </div>
      )}

      {/* API Keys */}
      {tab === 'users' && <UsersTab api={api} currentUser={user} />}

      {tab === 'api-keys' && (
        <Section title="API Keys & Integrations">
          <p className="text-sm text-muted-foreground mb-4">All API keys are stored securely and never exposed in the frontend.</p>
          <KeyField label="OpenAI API Key" masked={settings.openai_api_key_masked} value={settings.openai_api_key || ''} show={showKeys.openai}
            onToggle={() => setShowKeys(p => ({...p, openai: !p.openai}))} onChange={v => updateSetting('openai_api_key', v)} testId="setting-openai-key" />
          <SettingField label="OpenAI Model" value={settings.openai_model || 'gpt-4o'} onChange={v => updateSetting('openai_model', v)} testId="setting-openai-model"
            hint="e.g., gpt-4o, gpt-4o-mini, gpt-3.5-turbo" />
          <KeyField label="Resend API Key (Email)" masked={settings.resend_api_key_masked} value={settings.resend_api_key || ''} show={showKeys.resend}
            onToggle={() => setShowKeys(p => ({...p, resend: !p.resend}))} onChange={v => updateSetting('resend_api_key', v)} testId="setting-resend-key" />
          <SettingField label="Sender Email" value={settings.sender_email || ''} onChange={v => updateSetting('sender_email', v)} testId="setting-sender-email" />
          <KeyField label="WhatsApp Meta Token" masked={settings.whatsapp_meta_token_masked} value={settings.whatsapp_meta_token || ''} show={showKeys.whatsapp}
            onToggle={() => setShowKeys(p => ({...p, whatsapp: !p.whatsapp}))} onChange={v => updateSetting('whatsapp_meta_token', v)} testId="setting-whatsapp-token" />
          <SettingField label="WhatsApp Phone ID" value={settings.whatsapp_phone_id || ''} onChange={v => updateSetting('whatsapp_phone_id', v)} testId="setting-whatsapp-phone" />
          <KeyField label="Telegram Bot Token" masked={settings.telegram_bot_token_masked} value={settings.telegram_bot_token || ''} show={showKeys.telegram}
            onToggle={() => setShowKeys(p => ({...p, telegram: !p.telegram}))} onChange={v => updateSetting('telegram_bot_token', v)} testId="setting-telegram-token" />
          <SettingField label="Telegram Chat ID" value={settings.telegram_chat_id || ''} onChange={v => updateSetting('telegram_chat_id', v)} testId="setting-telegram-chat" />
          <KeyField label="YouTube Data API v3 Key" masked={settings.youtube_data_api_key_masked} value={settings.youtube_data_api_key || ''} show={showKeys.youtube}
            onToggle={() => setShowKeys(p => ({...p, youtube: !p.youtube}))} onChange={v => updateSetting('youtube_data_api_key', v)} testId="setting-youtube-key" />
          <p className="text-xs text-muted-foreground -mt-2 mb-4">Powers the auto-embedded recipe video in the Dish Detail view. Get a free key at <a className="underline text-[#FF6B35]" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer">console.cloud.google.com/apis/credentials</a> (enable &ldquo;YouTube Data API v3&rdquo; first). Without a key the dish view falls back to a &ldquo;Search on YouTube&rdquo; link.</p>
        </Section>
      )}

      {/* Notification Channels */}
      {tab === 'alerts' && (
        <Section title="Notification Channels">
          <p className="text-sm text-muted-foreground mb-4">Enable/disable notification channels. Configure API keys in the API Keys tab first.</p>
          {[
            { key: 'in_app', label: 'In-App Notifications', icon: Bell, desc: 'Always enabled. Shows alerts inside the app.' },
            { key: 'email', label: 'Email (Resend)', icon: EnvelopeSimple, desc: 'Send email notifications to family members.' },
            { key: 'telegram', label: 'Telegram Bot', icon: TelegramLogo, desc: 'Send alerts via Telegram bot.' },
            { key: 'whatsapp', label: 'WhatsApp (Meta API)', icon: WhatsappLogo, desc: 'Send WhatsApp messages via Meta Business API.' },
            { key: 'push', label: 'Push Notifications', icon: PushPin, desc: 'Browser push notifications.' },
          ].map(ch => (
            <div key={ch.key} className="flex items-center justify-between py-4 border-b border-border last:border-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <ch.icon size={20} weight="duotone" />
                </div>
                <div>
                  <p className="text-sm font-medium">{ch.label}</p>
                  <p className="text-xs text-muted-foreground">{ch.desc}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {ch.key === 'push' ? (
                  <button
                    onClick={async () => {
                      if (push.isSubscribed) {
                        await push.unsubscribe();
                        setTestResult({ type: 'success', msg: 'Push notifications disabled' });
                      } else {
                        const ok = await push.subscribe();
                        setTestResult({ type: ok ? 'success' : 'error', msg: ok ? 'Push notifications enabled!' : 'Failed — check browser permissions' });
                      }
                    }}
                    data-testid="toggle-push-btn"
                    className={`px-4 py-2 rounded-full text-sm font-medium ${push.isSubscribed ? 'bg-[#FF6B35] text-white btn-glow' : 'border border-border hover:bg-muted'}`}>
                    {push.isSubscribed ? 'Enabled' : push.isSupported ? 'Enable' : 'Not Supported'}
                  </button>
                ) : (
                  <>
                    <button onClick={() => testChannel(ch.key)} data-testid={`test-${ch.key}`}
                      className="px-3 py-1 rounded-lg text-xs border border-border hover:bg-muted transition-colors flex items-center gap-1">
                      <TestTube size={12} /> Test
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={settings.notification_channels?.[ch.key] || false}
                        onChange={e => updateChannel(ch.key, e.target.checked)}
                        disabled={ch.key === 'in_app'}
                        data-testid={`toggle-${ch.key}`}
                        className="sr-only peer" />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                    </label>
                  </>
                )}
              </div>
            </div>
          ))}

          <div className="border-t border-white/5 mt-6 pt-6">
            <h3 className="font-heading text-lg font-bold mb-4">Budget Alerts</h3>
            <div className="rounded-xl bg-muted/30 p-4 mb-6">
              <div className="flex items-start gap-3">
                <ShieldWarning size={20} weight="duotone" className="text-[#FF6B35] mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-1">Automated Budget Alerts</p>
                  <p className="text-xs text-muted-foreground">Get notified when your spending reaches the threshold.</p>
                </div>
              </div>
            </div>
            <SettingField label="Alert Threshold (%)" type="number" value={settings.budget_alert_threshold ?? 80} onChange={v => updateSetting('budget_alert_threshold', parseInt(v) || 80)} testId="setting-budget-threshold"
              hint="You'll be warned when any category reaches this % of its budget. 100% triggers a critical alert." />
            <div className="mb-4">
              <label className="overline block mb-3">Budget Alert Channels</label>
              <div className="space-y-2">
                {[
                  { key: 'in_app', label: 'In-App Notification', always: true },
                  { key: 'push', label: 'Push Notification' },
                  { key: 'email', label: 'Email' },
                  { key: 'telegram', label: 'Telegram' },
                ].map(ch => (
                  <div key={ch.key} className="flex items-center justify-between py-2 px-3 rounded-xl bg-muted/30">
                    <span className="text-sm font-medium">{ch.label}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox"
                        checked={ch.always || (settings.budget_alert_channels || ['in_app']).includes(ch.key)}
                        disabled={ch.always}
                        onChange={e => {
                          const channels = settings.budget_alert_channels || ['in_app'];
                          if (e.target.checked) {
                            updateSetting('budget_alert_channels', [...channels, ch.key]);
                          } else {
                            updateSetting('budget_alert_channels', channels.filter(c => c !== ch.key));
                          }
                        }}
                        data-testid={`budget-alert-ch-${ch.key}`}
                        className="sr-only peer" />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  setTestResult({ type: 'info', msg: 'Checking budgets...' });
                  const res = await api.post('/expenses/check-alerts');
                  const rd = res.data;
                  if (rd.alerts_sent > 0) {
                    setTestResult({ type: 'success', msg: `${rd.alerts_sent} budget alert(s) sent! Check your notifications.` });
                  } else {
                    setTestResult({ type: 'success', msg: 'All budgets within limits. No alerts needed.' });
                  }
                } catch (err) {
                  setTestResult({ type: 'error', msg: 'Failed to check alerts' });
                }
              }}
              data-testid="check-budget-alerts-btn"
              className="px-4 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
              <ShieldWarning size={18} /> Check Now
            </button>
            <p className="text-xs text-muted-foreground mt-2">Manually trigger a budget check and send alerts if thresholds are exceeded</p>
          </div>
        </Section>
      )}

      {/* Telegram Bot Setup */}
      {tab === 'bots' && (
        <Section title="Staff Check-in Bots">
          <div className="space-y-4">
            <div className="rounded-xl bg-muted/30 p-4">
              <p className="text-sm font-medium mb-2">Setup Instructions</p>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Create a Telegram bot via <span className="font-medium text-foreground">@BotFather</span> on Telegram</li>
                <li>Copy the bot token and paste it in Settings &gt; API Keys &gt; Telegram Bot Token</li>
                <li>Click "Activate Webhook" below to connect the bot to HomeCommand</li>
                <li>Share the bot with your staff. They use <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/link &lt;staff_id&gt;</span> to link their Telegram</li>
                <li>Staff can then use <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/checkin</span> and <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/checkout</span></li>
              </ol>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  try {
                    const res = await api.post('/bot/telegram/setup-webhook', {});
                    setTestResult({ type: res.data?.ok ? 'success' : 'error', msg: res.data?.ok ? 'Telegram webhook activated!' : (res.data?.error || res.data?.description || 'Failed') });
                  } catch (err) {
                    setTestResult({ type: 'error', msg: err.response?.data?.detail || err.response?.data?.error || 'Failed to setup webhook' });
                  }
                }}
                data-testid="setup-telegram-webhook-btn"
                className="px-4 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
                <TelegramLogo size={18} /> Activate Webhook
              </button>
              <p className="text-xs text-muted-foreground">Requires Telegram Bot Token in API Keys tab</p>
            </div>
            <div className="rounded-xl bg-muted/30 p-4">
              <p className="text-sm font-medium mb-2">Bot Commands</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/start</span><span className="text-muted-foreground">Welcome message</span>
                <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/link &lt;id&gt;</span><span className="text-muted-foreground">Link to staff profile</span>
                <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/checkin</span><span className="text-muted-foreground">Check in for today</span>
                <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/checkout</span><span className="text-muted-foreground">Check out for today</span>
                <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/status</span><span className="text-muted-foreground">View attendance</span>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 mt-8 pt-8">
            <h3 className="font-heading text-lg font-bold mb-4">WhatsApp Bot</h3>
            <div className="space-y-4">
              <div className="rounded-xl bg-muted/30 p-4">
                <div className="flex items-start gap-3">
                  <WhatsappLogo size={20} weight="duotone" className="text-[#25D366] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium mb-1">WhatsApp Staff Bot</p>
                    <p className="text-xs text-muted-foreground">Allow staff to check-in/check-out via WhatsApp, similar to the Telegram bot.</p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl bg-muted/30 p-4">
                <p className="text-sm font-medium mb-2">Setup Instructions</p>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Create a Meta Business App at <span className="font-medium text-foreground">developers.facebook.com</span></li>
                  <li>Enable WhatsApp product and get your Access Token & Phone Number ID</li>
                  <li>Paste both in Settings &gt; API Keys (WhatsApp Meta Token & Phone ID)</li>
                  <li>Click "Get Webhook Details" below to get your webhook URL and verify token</li>
                  <li>In Meta Developer Console &gt; WhatsApp &gt; Configuration, set the Callback URL and Verify Token</li>
                  <li>Subscribe to the <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">messages</span> webhook field</li>
                  <li>Staff message your WhatsApp Business number with <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">/link &lt;staff_id&gt;</span> to connect</li>
                </ol>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={async () => {
                    try {
                      const res = await api.post('/bot/whatsapp/setup-webhook', {});
                      if (res.data?.error) {
                        setTestResult({ type: 'error', msg: res.data.error });
                      } else {
                        setTestResult({ type: 'success', msg: `Webhook URL: ${res.data.webhook_url}\nVerify Token: ${res.data.verify_token}\n\n${res.data.instructions}` });
                      }
                    } catch (err) {
                      setTestResult({ type: 'error', msg: err.response?.data?.detail || 'Failed to setup webhook' });
                    }
                  }}
                  data-testid="setup-wa-webhook-btn"
                  className="px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-semibold flex items-center gap-2">
                  <WhatsappLogo size={18} /> Get Webhook Details
                </button>
                <p className="text-xs text-muted-foreground">Requires WhatsApp Token in API Keys tab</p>
              </div>
              <div className="rounded-xl bg-muted/30 p-4">
                <p className="text-sm font-medium mb-2">Bot Commands</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/help</span><span className="text-muted-foreground">Show commands</span>
                  <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/link &lt;id&gt;</span><span className="text-muted-foreground">Link to staff profile</span>
                  <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/checkin</span><span className="text-muted-foreground">Check in for today</span>
                  <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/checkout</span><span className="text-muted-foreground">Check out for today</span>
                  <span className="font-mono text-xs bg-muted px-2 py-1 rounded">/status</span><span className="text-muted-foreground">View attendance</span>
                </div>
              </div>
            </div>
          </div>
        </Section>
      )}


      {/* Smart Home (Home Assistant) */}
      {tab === 'devices' && (
        <Section title="Devices & Integrations">
          <h3 className="font-heading text-lg font-bold mb-4">Smart Home</h3>
          <div className="rounded-xl bg-muted/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <HouseLine size={20} weight="duotone" className="text-[#FF6B35] mt-0.5" />
              <div>
                <p className="text-sm font-medium mb-1">Home Assistant Setup</p>
                <p className="text-xs text-muted-foreground">Connect your Home Assistant server to control lights, fans, AC, and more from HomeCommand.</p>
              </div>
            </div>
          </div>
          <SettingField label="Home Assistant URL" value={settings.ha_url || ''} onChange={v => updateSetting('ha_url', v)} testId="setting-ha-url"
            hint="e.g., http://192.168.1.100:8123 or https://my-ha.duckdns.org" />
          <KeyField label="Long-Lived Access Token" masked={settings.ha_token_masked} value={settings.ha_token || ''} show={showKeys.ha_token}
            onToggle={() => setShowKeys(p => ({...p, ha_token: !p.ha_token}))} onChange={v => updateSetting('ha_token', v)} testId="setting-ha-token" />
          <div className="mt-4">
            <button
              onClick={async () => {
                try {
                  const res = await api.get('/iot/status');
                  setTestResult({ type: res.data?.connected ? 'success' : 'error', msg: res.data?.connected ? 'Connected to Home Assistant!' : (res.data?.message || 'Connection failed') });
                } catch (err) {
                  setTestResult({ type: 'error', msg: 'Connection test failed' });
                }
              }}
              data-testid="test-ha-connection-btn"
              className="px-4 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
              <HouseLine size={18} /> Test Connection
            </button>
          </div>
          <div className="mt-6 rounded-xl bg-muted/30 p-4">
            <p className="text-sm font-medium mb-2">How to get a Long-Lived Access Token</p>
            <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
              <li>Open your Home Assistant at the URL above</li>
              <li>Click your profile (bottom-left corner)</li>
              <li>Scroll to <span className="font-medium text-foreground">Long-Lived Access Tokens</span></li>
              <li>Click "Create Token", name it "HomeCommand", and copy it here</li>
            </ol>
          </div>

          <div className="border-t border-white/5 mt-8 pt-8">
            <h3 className="font-heading text-lg font-bold mb-4">Camera Surveillance</h3>
            <div className="rounded-xl bg-muted/30 p-4 mb-6">
              <div className="flex items-start gap-3">
                <Eye size={20} weight="duotone" className="text-[#00E5FF] mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-1">Auto-Scan Cameras</p>
                  <p className="text-xs text-muted-foreground">Automatically scan cameras for face detection. Choose between timed interval scans, motion-triggered scans, or both.</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Enable Auto-Scan</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Scans all cameras automatically</p>
                </div>
                <button data-testid="toggle-auto-scan"
                  onClick={() => updateSetting('camera_auto_scan_enabled', !settings.camera_auto_scan_enabled)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${settings.camera_auto_scan_enabled ? 'bg-[#FF6B35]' : 'bg-white/10'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${settings.camera_auto_scan_enabled ? 'left-[26px]' : 'left-0.5'}`} />
                </button>
              </div>
              <div>
                <label className="overline block mb-2">Scan Mode</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { value: 'interval', label: 'Interval', desc: 'Scans every X minutes' },
                    { value: 'motion', label: 'Motion', desc: 'Scans on motion detection' },
                    { value: 'both', label: 'Both', desc: 'Interval + Motion triggers' },
                  ].map(mode => (
                    <button key={mode.value} data-testid={`scan-mode-${mode.value}`}
                      onClick={() => updateSetting('camera_scan_mode', mode.value)}
                      className={`p-3 rounded-xl text-left transition-all ${(settings.camera_scan_mode || 'interval') === mode.value ? 'ring-2 ring-[#FF6B35] bg-[#FF6B35]/10' : 'bg-white/5 hover:bg-white/[0.07]'}`}>
                      <p className={`text-sm font-semibold ${(settings.camera_scan_mode || 'interval') === mode.value ? 'text-[#FF6B35]' : 'text-neutral-300'}`}>{mode.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{mode.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              {(settings.camera_scan_mode || 'interval') !== 'motion' && (
                <div>
                  <label className="overline block mb-2">Scan Interval</label>
                  <select value={settings.camera_scan_interval_minutes || 15}
                    onChange={e => updateSetting('camera_scan_interval_minutes', parseInt(e.target.value))}
                    data-testid="scan-interval-select"
                    className="w-full px-4 py-3 rounded-2xl bg-muted/50 border border-white/10 text-sm outline-none">
                    <option value={5}>Every 5 minutes</option>
                    <option value={10}>Every 10 minutes</option>
                    <option value={15}>Every 15 minutes (default)</option>
                    <option value={30}>Every 30 minutes</option>
                    <option value={60}>Every 60 minutes</option>
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1">More frequent scans use more GPT-4o Vision API credits</p>
                </div>
              )}
              {(settings.camera_scan_mode || 'interval') !== 'interval' && (
                <div className="rounded-xl bg-muted/30 p-4">
                  <p className="text-sm font-medium mb-2">Motion Sensor Setup</p>
                  <p className="text-xs text-muted-foreground mb-3">Link motion sensors to cameras. When motion is detected, that camera is scanned immediately. Configure in Face Recognition &gt; Detection Log.</p>
                  <div className="text-xs text-muted-foreground space-y-1.5">
                    <p className="font-medium text-foreground">Home Assistant Webhook:</p>
                    <code className="block bg-black/30 px-3 py-2 rounded-lg text-[11px] break-all">
                      POST {window.location.origin}/api/face-recognition/motion-webhook
                    </code>
                    <p className="mt-2">Body: <code className="bg-black/30 px-1.5 py-0.5 rounded text-[11px]">{'{"ha_entity_id": "binary_sensor.front_door_motion"}'}</code></p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-white/5 mt-8 pt-8">
            <h3 className="font-heading text-lg font-bold mb-4">Voice Assistants</h3>
          <div className="rounded-xl bg-muted/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <Microphone size={20} weight="duotone" className="text-[#FF6B35] mt-0.5" />
              <div>
                <p className="text-sm font-medium mb-1">Alexa & Google Home</p>
                <p className="text-xs text-muted-foreground">Control your smart home via voice assistants. Generate an API key and configure the webhook URL in your Alexa Skill or Google Action.</p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="overline block mb-2">Webhook URL</label>
            <div className="flex items-center gap-2">
              <input type="text" readOnly value={`${window.location.origin}/api/voice/webhook`} data-testid="voice-webhook-url"
                className="flex-1 px-4 py-3 rounded-2xl bg-muted/50 border border-white/10 text-sm text-muted-foreground font-mono" />
              <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/api/voice/webhook`)}
                className="p-3 rounded-2xl bg-muted/50 border border-white/10 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <Copy size={16} />
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Use this URL in your Alexa Custom Skill or IFTTT webhook</p>
          </div>

          <div className="mb-6">
            <label className="overline block mb-2">Voice API Key</label>
            <div className="flex items-center gap-2">
              <input type="text" readOnly value={settings.voice_api_key || 'Not generated yet'} data-testid="voice-api-key-display"
                className="flex-1 px-4 py-3 rounded-2xl bg-muted/50 border border-white/10 text-sm text-muted-foreground font-mono" />
              <button onClick={async () => {
                try {
                  const res = await api.get('/voice/key/generate');
                  updateSetting('voice_api_key', res.data.voice_api_key);
                  setTestResult({ type: 'success', msg: 'New voice API key generated!' });
                } catch (err) { setTestResult({ type: 'error', msg: 'Failed to generate key' }); }
              }} data-testid="generate-voice-key-btn"
                className="px-4 py-3 rounded-2xl bg-[#FF6B35] text-white text-sm font-semibold btn-glow">
                <Key size={16} />
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Include this in the <code className="text-[#FF6B35]">X-Voice-Key</code> header when calling the webhook</p>
          </div>

          <div className="rounded-xl bg-muted/30 p-5 space-y-4">
            <p className="text-sm font-medium">Setup Instructions</p>
            <div>
              <p className="text-xs font-semibold text-[#F59E0B] mb-1.5">Amazon Alexa</p>
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Go to <span className="text-foreground">developer.amazon.com/alexa</span> and create a Custom Skill</li>
                <li>Set the endpoint to the Webhook URL above (HTTPS)</li>
                <li>Add the <code className="text-[#FF6B35]">X-Voice-Key</code> header with your API key</li>
                <li>Map intents like "TurnOff" to send <code className="text-[#FF6B35]">{`{"intent":"turn off {device}"}`}</code></li>
              </ol>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#4285F4] mb-1.5">Google Home (via IFTTT)</p>
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Create an IFTTT applet with Google Assistant trigger</li>
                <li>Use "Webhooks" action pointing to the Webhook URL</li>
                <li>Set method to POST, Content-Type: application/json</li>
                <li>Add header <code className="text-[#FF6B35]">X-Voice-Key: your_key</code></li>
                <li>Body: <code className="text-[#FF6B35]">{`{"intent":"{{TextField}}","source":"google"}`}</code></li>
              </ol>
            </div>
          </div>
          </div>
        </Section>
      )}

      {/* TV Display & TTS */}
      {tab === 'display' && (
        <Section title="TV Display & Speaker Settings">
          <div className="rounded-xl bg-muted/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <Bell size={20} weight="duotone" className="text-[#00E5FF] mt-0.5" />
              <div>
                <p className="text-sm font-medium mb-1">Home Announcement System</p>
                <p className="text-xs text-muted-foreground">Configure TV kiosk displays, text-to-speech for announcements, and weather data.</p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="overline block mb-2">TV Display URL</label>
            <div className="flex items-center gap-2">
              <input type="text" readOnly value={`${window.location.origin}/display?room=Living+Room`} data-testid="display-url-field"
                className="flex-1 px-4 py-3 rounded-2xl bg-muted/50 border border-white/10 text-sm text-muted-foreground font-mono" />
              <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/display`)}
                className="p-3 rounded-2xl bg-muted/50 border border-white/10 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <Copy size={16} />
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Open this URL on your Smart TV browser. Add <code className="text-[#FF6B35]">?room=Kitchen</code> for room-specific content.</p>
          </div>

          <div className="mb-6">
            <label className="overline block mb-2">Text-to-Speech Provider</label>
            <select value={settings.tts_provider || 'browser'} onChange={e => updateSetting('tts_provider', e.target.value)}
              data-testid="setting-tts-provider"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50">
              <option value="browser">Browser Web Speech API (Free, default)</option>
              <option value="openai">OpenAI TTS (Requires OpenAI API key)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Browser speech works on most Smart TVs with Chrome. OpenAI TTS provides higher quality natural voice.</p>
          </div>

          {(settings.tts_provider === 'openai') && (
            <div className="mb-6">
              <SettingField label="TTS Voice" value={settings.tts_voice || 'alloy'} onChange={v => updateSetting('tts_voice', v)} testId="setting-tts-voice"
                hint="Options: alloy, echo, fable, onyx, nova, shimmer" />
            </div>
          )}

          <div className="border-t border-white/5 mt-6 pt-6">
            <p className="overline mb-3">Weather (for TV Display)</p>
            <SettingField label="OpenWeatherMap API Key" value={settings.weather_api_key || ''} onChange={v => updateSetting('weather_api_key', v)} testId="setting-weather-key"
              hint="Get a free key at openweathermap.org/api" />
            <SettingField label="City" value={settings.weather_city || ''} onChange={v => updateSetting('weather_city', v)} testId="setting-weather-city"
              hint="e.g., Mumbai, Delhi, Ahmedabad" />
          </div>

          <div className="border-t border-white/5 mt-6 pt-6">
            <p className="overline mb-3">Kiosk Mode</p>
            <p className="text-xs text-muted-foreground mb-3">Set a PIN to prevent anyone from navigating away from the TV display. Leave empty to disable kiosk lock.</p>
            <SettingField label="Kiosk PIN (4-6 digits)" value={settings.kiosk_pin || ''} onChange={v => updateSetting('kiosk_pin', v.replace(/\D/g, '').slice(0, 6))} testId="setting-kiosk-pin" hint="Only numeric digits. Leave blank to disable kiosk lock." />
          </div>

          <div className="border-t border-white/5 mt-6 pt-6">
            <p className="overline mb-3">Display Rooms</p>
            <p className="text-xs text-muted-foreground mb-3">Default rooms: Living Room, Kitchen, Bedroom, Hall, Office. Each room can show different content based on cameras and announcements.</p>
            <div className="flex flex-wrap gap-2">
              {(settings.display_rooms || ['Living Room', 'Kitchen', 'Bedroom', 'Hall', 'Office']).map((room, i) => (
                <span key={i} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[#00E5FF]/20 bg-[#00E5FF]/10 text-[#00E5FF]">{room}</span>
              ))}
            </div>
          </div>

          <div className="border-t border-white/5 mt-6 pt-6">
            <p className="overline mb-3">News Ticker (RSS Feeds)</p>
            <p className="text-xs text-muted-foreground mb-3">Headlines scroll at the bottom of the TV Display. Toggle feeds on/off.</p>
            <div className="space-y-2">
              {(settings.news_feeds || [
                {name: 'Times of India', url: 'https://timesofindia.indiatimes.com/rssfeedstopstories.cms', enabled: true},
                {name: 'NDTV', url: 'https://feeds.feedburner.com/ndtvnews-top-stories', enabled: true},
                {name: 'Hindustan Times', url: 'https://www.hindustantimes.com/feeds/rss/india-news/rssfeed.xml', enabled: true},
                {name: 'Indian Express', url: 'https://indianexpress.com/section/india/feed/', enabled: true},
                {name: 'Mint', url: 'https://www.livemint.com/rss/news', enabled: false},
              ]).map((feed, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/3 border border-white/5">
                  <div className="flex items-center gap-3 min-w-0">
                    <Newspaper size={14} className="text-[#FF6B35] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{feed.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{feed.url}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const feeds = settings.news_feeds || [
                        {name: 'Times of India', url: 'https://timesofindia.indiatimes.com/rssfeedstopstories.cms', enabled: true},
                        {name: 'NDTV', url: 'https://feeds.feedburner.com/ndtvnews-top-stories', enabled: true},
                        {name: 'Hindustan Times', url: 'https://www.hindustantimes.com/feeds/rss/india-news/rssfeed.xml', enabled: true},
                        {name: 'Indian Express', url: 'https://indianexpress.com/section/india/feed/', enabled: true},
                        {name: 'Mint', url: 'https://www.livemint.com/rss/news', enabled: false},
                      ];
                      feeds[i] = { ...feeds[i], enabled: !feeds[i].enabled };
                      updateSetting('news_feeds', feeds);
                    }}
                    data-testid={`toggle-feed-${feed.name.replace(/\s/g, '-').toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-all ${
                      feed.enabled !== false
                        ? 'border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E]'
                        : 'border-white/10 bg-white/3 text-neutral-500'
                    }`}>
                    {feed.enabled !== false ? 'ON' : 'OFF'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-white/5 mt-6 pt-6">
            <div className="flex items-center gap-2 mb-3">
              <HandWaving size={18} weight="duotone" className="text-[#FF6B35]" />
              <p className="overline">Guest Mode</p>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Configure the guest welcome screen shown at <code className="text-[#FF6B35]">/display?mode=guest</code>. Open this URL on a tablet or TV in the guest room.</p>

            <div className="mb-4">
              <label className="overline block mb-2">Guest Display URL</label>
              <div className="flex items-center gap-2">
                <input type="text" readOnly value={`${window.location.origin}/display?mode=guest`} data-testid="guest-display-url"
                  className="flex-1 px-4 py-3 rounded-2xl bg-muted/50 border border-white/10 text-sm text-muted-foreground font-mono" />
                <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/display?mode=guest`)}
                  className="p-3 rounded-2xl bg-muted/50 border border-white/10 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <SettingField label="Welcome Message" value={settings.guest_welcome_message || 'Welcome to our home!'} onChange={v => updateSetting('guest_welcome_message', v)} testId="setting-guest-welcome" hint="Displayed prominently on the guest screen" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <SettingField label="Guest Wi-Fi Name (SSID)" value={settings.guest_wifi_ssid || ''} onChange={v => updateSetting('guest_wifi_ssid', v)} testId="setting-guest-wifi-ssid" hint="Network name for guests" />
              <SettingField label="Guest Wi-Fi Password" value={settings.guest_wifi_password || ''} onChange={v => updateSetting('guest_wifi_password', v)} testId="setting-guest-wifi-pass" hint="Shown on guest display" />
            </div>

            {/* House Rules */}
            <div className="mb-4">
              <label className="overline block mb-2">House Rules</label>
              <div className="space-y-2 mb-2">
                {(settings.guest_house_rules || []).map((rule, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[#FF6B35] text-sm font-bold w-6 text-center">{i + 1}</span>
                    <input type="text" value={rule} onChange={e => {
                      const rules = [...(settings.guest_house_rules || [])];
                      rules[i] = e.target.value;
                      updateSetting('guest_house_rules', rules);
                    }} data-testid={`setting-house-rule-${i}`}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <button onClick={() => updateSetting('guest_house_rules', (settings.guest_house_rules || []).filter((_, idx) => idx !== i))}
                      className="p-1.5 text-muted-foreground hover:text-red-400"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => updateSetting('guest_house_rules', [...(settings.guest_house_rules || []), ''])}
                data-testid="add-house-rule-btn"
                className="px-4 py-2 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-2">
                <Plus size={14} /> Add Rule
              </button>
            </div>

            {/* Emergency Contacts */}
            <div className="mb-4">
              <label className="overline block mb-2">Emergency Contacts</label>
              <div className="space-y-2 mb-2">
                {(settings.guest_emergency_contacts || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Phone size={14} className="text-red-400 flex-shrink-0" />
                    <input type="text" value={c.name || ''} placeholder="Name" onChange={e => {
                      const contacts = [...(settings.guest_emergency_contacts || [])];
                      contacts[i] = { ...contacts[i], name: e.target.value };
                      updateSetting('guest_emergency_contacts', contacts);
                    }} data-testid={`setting-emergency-name-${i}`}
                      className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <input type="text" value={c.phone || ''} placeholder="Phone" onChange={e => {
                      const contacts = [...(settings.guest_emergency_contacts || [])];
                      contacts[i] = { ...contacts[i], phone: e.target.value };
                      updateSetting('guest_emergency_contacts', contacts);
                    }} data-testid={`setting-emergency-phone-${i}`}
                      className="w-40 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <input type="text" value={c.relation || ''} placeholder="Relation" onChange={e => {
                      const contacts = [...(settings.guest_emergency_contacts || [])];
                      contacts[i] = { ...contacts[i], relation: e.target.value };
                      updateSetting('guest_emergency_contacts', contacts);
                    }} data-testid={`setting-emergency-relation-${i}`}
                      className="w-28 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <button onClick={() => updateSetting('guest_emergency_contacts', (settings.guest_emergency_contacts || []).filter((_, idx) => idx !== i))}
                      className="p-1.5 text-muted-foreground hover:text-red-400"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => updateSetting('guest_emergency_contacts', [...(settings.guest_emergency_contacts || []), { name: '', phone: '', relation: '' }])}
                data-testid="add-emergency-contact-btn"
                className="px-4 py-2 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-2">
                <Plus size={14} /> Add Contact
              </button>
            </div>

            {/* Nearby Places */}
            <div className="mb-4">
              <label className="overline block mb-2">Nearby Places</label>
              <div className="space-y-2 mb-2">
                {(settings.guest_nearby_places || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <MapPin size={14} className="text-[#22C55E] flex-shrink-0" />
                    <input type="text" value={p.name || ''} placeholder="Place name" onChange={e => {
                      const places = [...(settings.guest_nearby_places || [])];
                      places[i] = { ...places[i], name: e.target.value };
                      updateSetting('guest_nearby_places', places);
                    }} data-testid={`setting-nearby-name-${i}`}
                      className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <input type="text" value={p.type || ''} placeholder="Type (Hospital, ATM...)" onChange={e => {
                      const places = [...(settings.guest_nearby_places || [])];
                      places[i] = { ...places[i], type: e.target.value };
                      updateSetting('guest_nearby_places', places);
                    }} data-testid={`setting-nearby-type-${i}`}
                      className="w-36 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <input type="text" value={p.distance || ''} placeholder="Distance" onChange={e => {
                      const places = [...(settings.guest_nearby_places || [])];
                      places[i] = { ...places[i], distance: e.target.value };
                      updateSetting('guest_nearby_places', places);
                    }} data-testid={`setting-nearby-dist-${i}`}
                      className="w-24 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <button onClick={() => updateSetting('guest_nearby_places', (settings.guest_nearby_places || []).filter((_, idx) => idx !== i))}
                      className="p-1.5 text-muted-foreground hover:text-red-400"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => updateSetting('guest_nearby_places', [...(settings.guest_nearby_places || []), { name: '', type: '', distance: '' }])}
                data-testid="add-nearby-place-btn"
                className="px-4 py-2 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-2">
                <Plus size={14} /> Add Place
              </button>
            </div>

            {/* Local Transport */}
            <div className="mb-4">
              <label className="overline block mb-2">Local Transport</label>
              <div className="space-y-2 mb-2">
                {(settings.guest_local_transport || []).map((tr, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Car size={14} className="text-[#3B82F6] flex-shrink-0" />
                    <input type="text" value={tr.type || ''} placeholder="Type (Auto, Metro...)" onChange={e => {
                      const transport = [...(settings.guest_local_transport || [])];
                      transport[i] = { ...transport[i], type: e.target.value };
                      updateSetting('guest_local_transport', transport);
                    }} data-testid={`setting-transport-type-${i}`}
                      className="w-36 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <input type="text" value={tr.details || ''} placeholder="Details (available at gate, call 98xxx...)" onChange={e => {
                      const transport = [...(settings.guest_local_transport || [])];
                      transport[i] = { ...transport[i], details: e.target.value };
                      updateSetting('guest_local_transport', transport);
                    }} data-testid={`setting-transport-details-${i}`}
                      className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                    <button onClick={() => updateSetting('guest_local_transport', (settings.guest_local_transport || []).filter((_, idx) => idx !== i))}
                      className="p-1.5 text-muted-foreground hover:text-red-400"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => updateSetting('guest_local_transport', [...(settings.guest_local_transport || []), { type: '', details: '' }])}
                data-testid="add-transport-btn"
                className="px-4 py-2 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-2">
                <Plus size={14} /> Add Transport
              </button>
            </div>

            {/* House Map */}
            <div className="mb-4">
              <label className="overline block mb-2">House Map / Floor Plan</label>
              <div className="flex gap-3 items-start">
                {settings.guest_house_map_url && (
                  <div className="w-24 h-24 rounded-xl border border-white/10 overflow-hidden bg-white/5 flex items-center justify-center flex-shrink-0">
                    <img src={settings.guest_house_map_url} alt="Map" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <input type="text" value={settings.guest_house_map_url || ''} onChange={e => updateSetting('guest_house_map_url', e.target.value)}
                    data-testid="setting-house-map-url" placeholder="Image URL or upload below"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
                  <div className="flex gap-2 mt-2">
                    <label className="px-3 py-1.5 rounded-lg bg-white/5 text-xs text-neutral-400 cursor-pointer hover:bg-white/10 flex items-center gap-1" data-testid="upload-house-map-btn">
                      <Image size={12} /> Upload Image
                      <input type="file" accept="image/*" className="hidden" onChange={e => {
                        const file = e.target.files?.[0]; if (!file) return;
                        const reader = new FileReader(); reader.onload = () => updateSetting('guest_house_map_url', reader.result); reader.readAsDataURL(file);
                      }} />
                    </label>
                    {settings.guest_house_map_url && <button onClick={() => updateSetting('guest_house_map_url', '')} className="px-3 py-1.5 rounded-lg bg-red-500/10 text-xs text-red-400 hover:bg-red-500/20">Remove</button>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Upload a floor plan or map image. Guests can see it on the welcome screen.</p>
                </div>
              </div>
            </div>
          </div>
        </Section>
      )}


      {/* WhatsApp & Telegram Messaging */}
      {tab === 'messaging' && (
        <Section title="WhatsApp & Telegram Notifications">
          <div className="rounded-xl bg-muted/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <WhatsappLogo size={20} weight="duotone" className="text-[#25D366] mt-0.5" />
              <div>
                <p className="text-sm font-medium mb-1">WhatsApp Business API</p>
                <p className="text-xs text-muted-foreground">Send notifications via WhatsApp. Requires a Meta Business account with WhatsApp API access.</p>
              </div>
            </div>
          </div>
          <KeyField label="WhatsApp Access Token" masked={settings.whatsapp_token_masked} value={settings.whatsapp_token || ''} show={showKeys.whatsapp_token}
            onToggle={() => setShowKeys(p => ({...p, whatsapp_token: !p.whatsapp_token}))} onChange={v => updateSetting('whatsapp_token', v)} testId="setting-wa-token" />
          <SettingField label="Phone Number ID" value={settings.whatsapp_phone_number_id || ''} onChange={v => updateSetting('whatsapp_phone_number_id', v)} testId="setting-wa-phone-id"
            hint="The Phone Number ID from Meta Business Manager" />
          <SettingField label="Recipient Phone" value={settings.whatsapp_recipient || ''} onChange={v => updateSetting('whatsapp_recipient', v)} testId="setting-wa-recipient"
            hint="e.g., 919876543210 (country code + number, no +)" />
          <div className="mt-4">
            <button onClick={async () => {
              try {
                setTestResult({ type: 'info', msg: 'Sending test WhatsApp...' });
                await api.post('/notifications/send', { title: 'HomeCommand Test', message: 'WhatsApp notification is working!', channels: ['whatsapp'] });
                setTestResult({ type: 'success', msg: 'WhatsApp test sent! Check your phone.' });
              } catch (err) { setTestResult({ type: 'error', msg: err.response?.data?.detail || 'WhatsApp test failed' }); }
            }} data-testid="test-wa-btn"
              className="px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-semibold flex items-center gap-2">
              <WhatsappLogo size={18} /> Test WhatsApp
            </button>
          </div>

          <div className="border-t border-white/5 mt-8 pt-8">
            <div className="rounded-xl bg-muted/30 p-4 mb-6">
              <div className="flex items-start gap-3">
                <TelegramLogo size={20} weight="duotone" className="text-[#0088cc] mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-1">Telegram Notifications</p>
                  <p className="text-xs text-muted-foreground">Send alerts to a Telegram chat. Use @BotFather to create a bot.</p>
                </div>
              </div>
            </div>
            <KeyField label="Telegram Bot Token" masked={settings.telegram_bot_token_masked} value={settings.telegram_bot_token || ''} show={showKeys.telegram_bot_token}
              onToggle={() => setShowKeys(p => ({...p, telegram_bot_token: !p.telegram_bot_token}))} onChange={v => updateSetting('telegram_bot_token', v)} testId="setting-tg-token" />
            <SettingField label="Chat ID" value={settings.telegram_chat_id || ''} onChange={v => updateSetting('telegram_chat_id', v)} testId="setting-tg-chat-id"
              hint="Your Telegram chat ID (use @userinfobot to find it)" />
            <div className="mt-4">
              <button onClick={async () => {
                try {
                  setTestResult({ type: 'info', msg: 'Sending test Telegram...' });
                  await api.post('/notifications/send', { title: 'HomeCommand Test', message: 'Telegram notification is working!', channels: ['telegram'] });
                  setTestResult({ type: 'success', msg: 'Telegram test sent! Check your chat.' });
                } catch (err) { setTestResult({ type: 'error', msg: err.response?.data?.detail || 'Telegram test failed' }); }
              }} data-testid="test-tg-btn"
                className="px-4 py-2.5 rounded-full bg-[#0088cc] text-white text-sm font-semibold flex items-center gap-2">
                <TelegramLogo size={18} /> Test Telegram
              </button>
            </div>
          </div>
        </Section>
      )}

      {/* Expense Categories & Roles */}
      {tab === 'data' && (
        <Section title="Categories & Roles">
          <p className="text-sm text-muted-foreground mb-4">Customize expense categories used across the app.</p>
          <div className="space-y-2 mb-4">
            {settings.expense_categories?.map(cat => (
              <div key={cat.id} className="flex items-center justify-between py-2 px-3 rounded-xl bg-muted/30">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                  <span className="text-sm font-medium">{cat.name}</span>
                </div>
                <button onClick={() => removeCategory(cat.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
              </div>
            ))}
          </div>
          <button onClick={addCategory} data-testid="add-category-btn" className="px-4 py-2 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-2">
            <Plus size={16} /> Add Category
          </button>

          <div className="border-t border-white/5 mt-6 pt-6">
            <h3 className="font-heading text-lg font-bold mb-4">Staff Roles</h3>
            <p className="text-sm text-muted-foreground mb-4">Define available staff roles.</p>
            <div className="space-y-2 mb-4">
              {settings.staff_roles?.map((role, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-xl bg-muted/30">
                  <span className="text-sm font-medium">{role}</span>
                  <button onClick={() => removeRole(i)} className="p-1 text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <button onClick={addRole} data-testid="add-role-btn" className="px-4 py-2 rounded-full border border-dashed border-border text-sm text-muted-foreground hover:bg-muted flex items-center gap-2">
              <Plus size={16} /> Add Role
            </button>
          </div>
        </Section>
      )}

      {tab === 'backup' && (
        <BackupRestoreSection api={api} />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="glass-card p-6">
      <h3 className="font-heading text-lg font-bold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function SettingField({ label, value, onChange, type = 'text', testId, hint }) {
  return (
    <div className="mb-4">
      <label className="overline block mb-2">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 focus:bg-background transition-all" />
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function KeyField({ label, masked, value, show, onToggle, onChange, testId }) {
  return (
    <div className="mb-4">
      <label className="overline block mb-2">{label}</label>
      <div className="relative">
        <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
          placeholder={masked || 'Enter key...'}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30 pr-12 transition-all" />
        <button type="button" onClick={onToggle} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
          {show ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {masked && <p className="text-xs text-muted-foreground mt-1">Current: {masked}</p>}
    </div>
  );
}

function TwoFASetup({ api }) {
  const [status, setStatus] = React.useState(null);
  const [qrData, setQrData] = React.useState(null);
  const [code, setCode] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.get('/auth/2fa/status').then(r => { setStatus(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [api]);

  const handleSetup = async () => {
    try {
      const res = await api.post('/auth/2fa/setup');
      setQrData(res.data);
      setMsg('');
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
  };

  const handleVerify = async () => {
    try {
      await api.post('/auth/2fa/verify', { code });
      setStatus({ enabled: true });
      setQrData(null);
      setCode('');
      setMsg('2FA enabled successfully!');
    } catch (e) { setMsg(e.response?.data?.detail || 'Invalid code'); }
  };

  const handleDisable = async () => {
    if (!code) { setMsg('Enter your authenticator code to disable 2FA'); return; }
    try {
      await api.post('/auth/2fa/disable', { code });
      setStatus({ enabled: false });
      setCode('');
      setMsg('2FA disabled');
    } catch (e) { setMsg(e.response?.data?.detail || 'Invalid code'); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div data-testid="2fa-settings">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${status?.enabled ? 'bg-[#22C55E] shadow-[0_0_8px_rgba(34,197,94,0.5)]' : 'bg-neutral-600'}`} />
          <p className="text-sm font-medium">{status?.enabled ? '2FA is Active' : '2FA is Off'}</p>
        </div>
        {!status?.enabled && !qrData && (
          <button onClick={handleSetup} data-testid="enable-2fa-btn"
            className="px-4 py-2 rounded-full bg-[#00E5FF] text-black text-sm font-semibold">
            Enable 2FA
          </button>
        )}
      </div>

      {qrData && (
        <div className="space-y-4 animate-fade-up">
          <p className="text-sm text-muted-foreground">Scan this QR code with Google Authenticator, Authy, or similar app:</p>
          <div className="flex justify-center">
            <img src={qrData.qr_code} alt="2FA QR Code" className="w-48 h-48 rounded-xl bg-white p-2" data-testid="2fa-qr-code" />
          </div>
          <p className="text-xs text-muted-foreground text-center">Manual key: <code className="text-[#FF6B35] font-mono">{qrData.secret}</code></p>
          <div>
            <label className="overline block mb-2">Verification Code</label>
            <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="2fa-verify-code" placeholder="000000" maxLength={6}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-center tracking-widest font-mono outline-none focus:border-[#00E5FF]/50" />
          </div>
          <button onClick={handleVerify} disabled={code.length < 6} data-testid="verify-2fa-setup-btn"
            className="w-full py-3 rounded-full bg-[#00E5FF] text-black text-sm font-semibold disabled:opacity-50">
            Verify & Activate
          </button>
        </div>
      )}

      {status?.enabled && (
        <div className="space-y-3 mt-4 pt-4 border-t border-white/5">
          <p className="text-xs text-muted-foreground">To disable 2FA, enter your current authenticator code:</p>
          <div className="flex gap-2">
            <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="2fa-disable-code" placeholder="000000" maxLength={6}
              className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm font-mono outline-none" />
            <button onClick={handleDisable} data-testid="disable-2fa-btn"
              className="px-4 py-2.5 rounded-xl border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/10 transition-colors">
              Disable 2FA
            </button>
          </div>
        </div>
      )}

      {msg && <p className={`text-sm mt-3 px-3 py-2 rounded-lg ${msg.includes('success') || msg.includes('enabled') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`} data-testid="2fa-message">{msg}</p>}
    </div>
  );
}


function ChangePasswordForm({ api }) {
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [err, setErr] = React.useState('');

  const submit = async (e) => {
    e.preventDefault();
    setMsg(''); setErr('');
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      setMsg('Password changed successfully');
      setCurrent(''); setNext('');
    } catch (error) {
      setErr(error?.response?.data?.detail || 'Could not change password');
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3 max-w-sm" data-testid="change-password-form">
      <input type="password" value={current} onChange={e => setCurrent(e.target.value)} required placeholder="Current password"
        data-testid="current-password-input" autoComplete="current-password"
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
      <input type="password" value={next} onChange={e => setNext(e.target.value)} required minLength={8} placeholder="New password (min 8 characters)"
        data-testid="new-password-input" autoComplete="new-password"
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
      {msg && <p className="text-xs text-[#22C55E]" data-testid="password-change-success">{msg}</p>}
      {err && <p className="text-xs text-red-500" data-testid="password-change-error">{err}</p>}
      <button type="submit" data-testid="change-password-btn" className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">
        Update Password
      </button>
    </form>
  );
}
